import { useCallback } from 'react';
import {
  findAllTaskListItems,
  getFindAllTaskListItemsQueryKey,
  useClaimTask as useClaimTaskApi,
} from '@api';
import { useNotification } from '@common';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

const TRANS_KEY_NOTIFICATION = 'NOTIFICATIONS.CLAIM_TASK';
const TASKS_QUERY_KEY = getFindAllTaskListItemsQueryKey();

type TasksListCacheType = Awaited<ReturnType<typeof findAllTaskListItems>>;

type ClaimTaskContextType = {
  previous: TasksListCacheType | undefined;
};

type UseClaimTaskArgsType = {
  currentUser: {
    id: string;
    firstname: string;
    lastname: string;
  };
};

type UseClaimTaskReturnType = {
  claimTask: (taskId: string) => void;
};

export const useClaimTask = ({
  currentUser,
}: UseClaimTaskArgsType): UseClaimTaskReturnType => {
  const queryClient = useQueryClient();
  const { showError } = useNotification();

  const updateCache = useCallback(
    async (taskId: string) => {
      await queryClient.cancelQueries({ queryKey: TASKS_QUERY_KEY });

      const previous =
        queryClient.getQueryData<TasksListCacheType>(TASKS_QUERY_KEY);

      const claimedBy = {
        id: currentUser.id,
        name: `${currentUser.firstname} ${currentUser.lastname}`,
        type: 'PERSON' as const,
      };
      const claimedAt = new Date().toISOString();

      queryClient.setQueryData<TasksListCacheType>(
        TASKS_QUERY_KEY,
        (current) => {
          if (!current) return current;

          return {
            ...current,
            data: current.data.map((task) =>
              task.id === taskId ? { ...task, claimedBy, claimedAt } : task
            ),
          };
        }
      );

      return { previous };
    },
    [currentUser, queryClient]
  );

  const { mutate } = useClaimTaskApi<AxiosError, ClaimTaskContextType>({
    mutation: {
      onMutate: async ({ taskId }) => updateCache(taskId),
      onError: (error, _variables, context) => {
        queryClient.setQueryData(TASKS_QUERY_KEY, context?.previous);

        showError({
          title: `${TRANS_KEY_NOTIFICATION}.ERROR.TITLE`,
          message: `${TRANS_KEY_NOTIFICATION}.ERROR.MESSAGE`,
          error,
        });
      },
      onSettled: () => {
        void queryClient.invalidateQueries({ queryKey: TASKS_QUERY_KEY });
      },
    },
  });

  const claimTask = useCallback(
    (taskId: string) => {
      mutate({ taskId });
    },
    [mutate]
  );

  return { claimTask };
};
